import type { PairLifecycleStatus, PairListItem } from '../../../services/pairTradingApi';
import type { StockInstrument } from '../../../types';

/** Tab key of the unsaved, client-only "New pair" tab (also its `?pair=` value). */
export const NEW_PAIR_KEY = 'new';

/** ENDING and ENDED pairs can no longer be edited, funded or started. */
export function isPairReadOnly(status: PairLifecycleStatus | null | undefined): boolean {
    return status === 'ENDING' || status === 'ENDED';
}

const bareSymbol = (s: string) => s.split(':').pop() ?? s;

/** Name, else "HPG·FPT / VN30F2610", else a short id. */
export function pairDisplayName(item: Pick<PairListItem, 'name' | 'stockSymbols' | 'futureSymbol' | 'dataConfigId'>): string {
    if (item.name && item.name.trim()) return item.name.trim();
    const stocks = item.stockSymbols.map(bareSymbol).join('·');
    const future = item.futureSymbol ? bareSymbol(item.futureSymbol) : '';
    if (stocks || future) return [stocks || '—', future].filter(Boolean).join(' / ');
    return `Pair ${item.dataConfigId.slice(0, 6)}`;
}

export function pairStatusLabel(item: Pick<PairListItem, 'lifecycleStatus' | 'running'>): string {
    if (item.running) return 'Running';
    switch (item.lifecycleStatus) {
        case 'DRAFT': return 'Draft';
        case 'ACTIVE': return 'Active';
        case 'ENDING': return 'Ending';
        case 'ENDED': return 'Ended';
        default: return String(item.lifecycleStatus);
    }
}

const newestFirst = (a: PairListItem, b: PairListItem) =>
    Date.parse(b.createdAt) - Date.parse(a.createdAt);

/**
 * Which pair the page shows. `param` is the URL's `?pair=`; it wins when it names a
 * pair of this portfolio (or `new`). Otherwise: the portfolio's current pair, else the
 * newest open pair, else the newest pair at all, else the New pair tab.
 * Returns null while the list is not loaded yet, so nothing is fetched for a guess.
 */
export function resolveSelectedPairKey(args: {
    param: string | null;
    pairs: PairListItem[] | null;
    currentDataConfigId: string | null;
}): string | null {
    const { param, pairs, currentDataConfigId } = args;
    if (param === NEW_PAIR_KEY) return NEW_PAIR_KEY;
    if (pairs === null) return null;
    if (param && pairs.some((p) => p.dataConfigId === param)) return param;
    if (currentDataConfigId && pairs.some((p) => p.dataConfigId === currentDataConfigId)) {
        return currentDataConfigId;
    }
    const sorted = [...pairs].sort(newestFirst);
    const open = sorted.find((p) => p.lifecycleStatus !== 'ENDED');
    if (open) return open.dataConfigId;
    return NEW_PAIR_KEY;
}

export type PairOpenMode = 'IMMEDIATE' | 'REFERENCE';

/** Everything the config form holds — what a per-tab draft stores. */
export interface PairFormSnapshot {
    takeProfitReturn: number;
    cutLossReturn: number;
    maxOpen: number;
    activeSide: 'BUY' | 'SELL';
    futureInstrumentPrice: number | null;
    isPause: boolean;
    /** IMMEDIATE opens on start; REFERENCE waits for the reference-PnL trigger. */
    openMode: PairOpenMode;
    maxHoldingDays: number;
    stockProportion: number;
    derivPortfolioId: string | undefined;
    stockAllocatedCash: number | null;
    derivAllocatedCash: number | null;
    balanceMode: 'NOTIONAL_MATCH' | 'MANUAL';
    selectedStocks: StockInstrument[];
    /** Only for the New pair tab: the name it will be created with. */
    name?: string;
}

/** The page's initial form values — what "empty form" means. */
export const DEFAULT_PAIR_FORM: PairFormSnapshot = {
    takeProfitReturn: 0,
    cutLossReturn: 0,
    maxOpen: 1,
    activeSide: 'SELL',
    futureInstrumentPrice: null,
    isPause: false,
    // A new pair opens on start; reference prices are the advanced option.
    openMode: 'IMMEDIATE',
    maxHoldingDays: 0,
    stockProportion: 50,
    derivPortfolioId: undefined,
    stockAllocatedCash: null,
    derivAllocatedCash: null,
    balanceMode: 'NOTIONAL_MATCH',
    selectedStocks: [],
};

/**
 * "Copy to new pair": every setting carries over except the reference prices — the
 * values most easily forgotten or stale, so the user must type them again. Capital
 * belongs to the old pair and is not copied either.
 */
export function copySnapshotForNewPair(source: PairFormSnapshot): PairFormSnapshot {
    return {
        ...source,
        futureInstrumentPrice: null,
        stockAllocatedCash: null,
        derivAllocatedCash: null,
        isPause: false,
        selectedStocks: source.selectedStocks.map((s) => ({ ...s, referencePrice: null })),
        name: undefined,
    };
}

// ---------------------------------------------------------------------------
// Per-tab drafts, mirrored to sessionStorage so unsaved edits survive a reload.
// ---------------------------------------------------------------------------

const draftStorageKey = (portfolioId: string) => `pair:drafts:${portfolioId}`;

export function loadPairDrafts(portfolioId: string): Map<string, PairFormSnapshot> {
    try {
        const raw = window.sessionStorage.getItem(draftStorageKey(portfolioId));
        if (!raw) return new Map();
        const parsed = JSON.parse(raw) as Record<string, PairFormSnapshot>;
        return new Map(Object.entries(parsed ?? {}));
    } catch {
        return new Map();
    }
}

export function savePairDrafts(portfolioId: string, drafts: Map<string, PairFormSnapshot>): void {
    try {
        if (drafts.size === 0) {
            window.sessionStorage.removeItem(draftStorageKey(portfolioId));
        } else {
            window.sessionStorage.setItem(
                draftStorageKey(portfolioId),
                JSON.stringify(Object.fromEntries(drafts)),
            );
        }
    } catch {
        // Storage blocked or full: drafts still live in memory for this page.
    }
}
